import React, { useEffect, useRef, useState } from "react";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import { storage } from "../../../helpers/firebaseConfig";
import { groupHypothesisSteps, latestCausalFinding, methodEntries } from "./analysisMethods";
import "./LiveAnalysisWorkspace.css";

const EMPTY_DOCUMENT = Object.freeze({ status: "waiting", content: "", updatedAt: 0 });
export const TERMINAL = new Set(["completed", "completed_with_warnings", "failed", "timed_out", "cancelled"]);
const STAGE_LABELS = {
  queued: "Waiting for worker",
  starting: "Starting analysis",
  downloading_inputs: "Downloading inputs",
  inspecting_files: "Inspecting files",
  writing_code: "Writing analysis code…",
  running_code: "Running analysis…",
  running_agent: "Agent working…",
  uploading_artifacts: "Uploading outputs",
  presenting_result: "Presenting findings…",
  completed: "Analysis complete",
  completed_with_warnings: "Analysis complete with warnings",
  waiting_for_approval: "Waiting for approval",
  waiting_for_user: "Waiting for input",
  failed: "Analysis failed",
  timed_out: "Analysis timed out",
  cancelled: "Analysis cancelled",
};
const PYTHON_KEYWORDS = new Set([
  "and", "as", "assert", "async", "await", "break", "class", "continue", "def", "del",
  "elif", "else", "except", "False", "finally", "for", "from", "global", "if", "import",
  "in", "is", "lambda", "None", "nonlocal", "not", "or", "pass", "raise", "return",
  "True", "try", "while", "with", "yield",
]);

const currentStepLabel = (job) => job?.status === "cancelled" ? STAGE_LABELS.cancelled :
  STAGE_LABELS[job?.stage] || STAGE_LABELS[job?.status] ||
  String(job?.stage || job?.status || "Analysis in progress").replaceAll("_", " ");

function statusIcon(status, terminal) {
  if (status === "complete") return "✓";
  if (["generating", "streaming"].includes(status) && !terminal) return "●";
  if (["failed", "timed_out"].includes(terminal)) return "!";
  return "○";
}

function PythonLine({ line }) {
  const commentAt = line.indexOf("#");
  const source = commentAt >= 0 ? line.slice(0, commentAt) : line;
  const comment = commentAt >= 0 ? line.slice(commentAt) : "";
  const pieces = source.split(/(\b[A-Za-z_]\w*\b|\b\d+(?:\.\d+)?\b|"[^"\n]*"|'[^'\n]*')/g);
  return <>{pieces.filter((piece) => piece !== "").map((piece, index) => {
    let className = "";
    if (PYTHON_KEYWORDS.has(piece)) className = "isKeyword";
    else if (/^\d/.test(piece)) className = "isNumber";
    else if (/^["']/.test(piece)) className = "isString";
    return <span className={className} key={index}>{piece}</span>;
  })}{comment && <span className="isComment">{comment}</span>}</>;
}

function PythonDocument({ content }) {
  const lines = String(content || "").split(/\r?\n/);
  return <pre className="liveAnalysis-python"><code>{lines.map((line, index) => <React.Fragment key={index}><PythonLine line={line} />{index < lines.length - 1 ? "\n" : ""}</React.Fragment>)}</code></pre>;
}

function DocumentView({ document, terminalStatus }) {
  const status = document?.status || "waiting";
  const displayStatus = terminalStatus && ["generating", "streaming"].includes(status) ? "Latest draft" : status;
  return <section className={`liveAnalysis-documentView is-${status}`}>
    <header>
      <span className="liveAnalysis-statusIcon">{statusIcon(status, terminalStatus)}</span>
      <strong>Analysis code</strong>
      <small>{displayStatus}</small>
    </header>
    {document.content ? <PythonDocument content={document.content} />
      : <div className="liveAnalysis-empty">Content has not arrived yet.</div>}
  </section>;
}

export const analysisJobUpdatedAt = (job) => Math.max(
  Number(job?.analysis?.plan?.updatedAt || 0),
  Number(job?.analysis?.methods?.updatedAt || 0),
  Number(job?.analysis?.script?.updatedAt || 0),
  Number(job?.updatedAt || job?.createdAt || 0)
);

const activityItems = (job) => Object.entries(job?.activity || {})
  .map(([id, entry]) => ({ id, ...entry }))
  .sort((a, b) => Number(a.sequence || 0) - Number(b.sequence || 0));

const formatBytes = (value) => {
  const bytes = Number(value || 0);
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
};

function documentKindForActivity(entry) {
  const label = `${entry?.label || ""} ${entry?.detail || ""}`.toLowerCase();
  if ((label.includes("02_analysis.py") && /(shap|writ|generat|creat)/.test(label)) || label.includes("writing analysis code")) return "script";
  return "";
}

function ActivityTimeline({ job, onOpenDocument }) {
  const sourceItems = activityItems(job);
  const scriptEntry = sourceItems.find((entry) => String(entry.label || "").toLowerCase().includes("02_analysis.py") && documentKindForActivity(entry) === "script")
    || sourceItems.find((entry) => documentKindForActivity(entry) === "script");
  const items = [...sourceItems];
  if (!scriptEntry) items.push({ id: "analysis-script", label: "Analysis code", documentKind: "script" });
  const [expanded, setExpanded] = useState({});

  if (!items.length) return null;
  const active = !TERMINAL.has(job?.status);
  return <section className="liveAnalysis-activitySection">
    <div className="liveAnalysis-sectionHeading"><strong>Activity</strong><span>{items.length} steps</span></div>
    <div className="liveAnalysis-activity">
        {items.map((entry) => {
          const documentKind = entry.documentKind || (entry.id === scriptEntry?.id ? "script" : "");
          const detail = String(entry.detail || "");
          const isLong = detail.length > 240;
          const isExpanded = !!expanded[entry.id];
          const isCurrent = active && entry.id === sourceItems.at(-1)?.id;
          const rowIcon = isCurrent ? "●" : entry.documentKind ? "○" : "✓";
          return <div className="liveAnalysis-activityRow" key={entry.id}>
            <span className={isCurrent ? "isActive" : ""}>{rowIcon}</span>
            <div>
              <strong>{entry.label || currentStepLabel(entry)}</strong>
              {detail && <pre className={isExpanded ? "isExpanded" : ""}>{detail}</pre>}
              <footer>
                {entry.createdAt && <time>{new Date(entry.createdAt).toLocaleTimeString()}</time>}
                {isLong && <button type="button" onClick={() => setExpanded((old) => ({ ...old, [entry.id]: !isExpanded }))}>{isExpanded ? "Hide output" : "Show output"}</button>}
                {documentKind && <button className="liveAnalysis-rowAction" type="button" onClick={onOpenDocument}>View code →</button>}
              </footer>
            </div>
          </div>;
        })}
    </div>
  </section>;
}

function MethodsList({ title, items }) {
  if (!items.length) return null;
  return <section className="liveAnalysis-methodsGroup">
    <strong>{title}</strong>
    <ul>{items.map((item, index) => <li key={index}>
      {item.text}
      {item.details.length > 0 && <ul>{item.details.map((detail, detailIndex) => <li key={detailIndex}>{detail}</li>)}</ul>}
    </li>)}</ul>
  </section>;
}

// Steps are { step, details } objects; analyses recorded before details existed stored plain strings.
const methodsItem = (value) => typeof value === "string"
  ? { text: value, details: [] }
  : { text: value?.step || "", details: methodEntries(value?.details) };

function TestSection({ group, number }) {
  const root = methodsItem(group.root);
  const details = root.details.map(String);
  const data = details.filter((detail) => /^\s*Data\s*:/i.test(detail)).map((detail) => detail.replace(/^\s*Data\s*:\s*/i, ""));
  const tested = details.filter((detail) => /^\s*Tested\s*:/i.test(detail)).map((detail) => detail.replace(/^\s*Tested\s*:\s*/i, ""));
  const steps = [];
  const found = [];
  const next = [];
  group.steps.map(methodsItem).forEach((item) => {
    const match = item.text.match(/^\s*(Found|Next)\s*:\s*(.*)$/i);
    if (match?.[1].toLowerCase() === "found") found.push({ ...item, text: match[2] });
    else if (match?.[1].toLowerCase() === "next") next.push({ ...item, text: match[2] });
    else steps.push(item);
  });
  return <section className="liveAnalysis-hypothesis">
    <div className="liveAnalysis-hypothesisHeader"><span>Step {number}</span><strong>{root.text.replace(/^\s*Root cause\s*:\s*/i, "")}</strong></div>
    <MethodsList title="Data" items={data.map((text) => ({ text, details: [] }))} />
    <MethodsList title="Tested" items={tested.map((text) => ({ text, details: [] }))} />
    <MethodsList title="Steps" items={steps} />
    <MethodsList title="Found" items={found} />
    <MethodsList title="Next" items={next} />
  </section>;
}

function MethodsView({ methods, terminalStatus }) {
  const methodsReady = methods?.status === "complete";
  if (!methodsReady) {
    return <div className="liveAnalysis-empty">{terminalStatus
      ? "No methods recorded for this analysis."
      : "Methods will appear when the analysis finishes."}</div>;
  }
  const list = (value) => methodEntries(value).map(methodsItem).filter((item) => item.text);
  const stepGroups = groupHypothesisSteps(methods.steps);
  const hasHypotheses = stepGroups.some((group) => group.root);
  return <section className="liveAnalysis-methods">
    <MethodsList title="Data" items={list([methods.data])} />
    {hasHypotheses ? stepGroups.map((group, index) => group.root
      ? <TestSection key={index} group={group} number={stepGroups.slice(0, index + 1).filter((item) => item.root).length} />
      : <MethodsList key={index} title="Steps" items={list(group.steps)} />)
      : <MethodsList title="Steps" items={list(methods.steps)} />}
    <MethodsList title="Assumptions" items={list(methods.assumptions)} />
  </section>;
}

function GeneratedFiles({ artifacts = [] }) {
  const [downloading, setDownloading] = useState({});
  if (!artifacts.length) return null;
  const download = async (artifact) => {
    const path = artifact.storagePath || artifact.path;
    if (!path) return;
    setDownloading((old) => ({ ...old, [artifact.id]: true }));
    try {
      window.open(await getDownloadURL(storageRef(storage, path)), "_blank", "noopener,noreferrer");
    } finally {
      setDownloading((old) => ({ ...old, [artifact.id]: false }));
    }
  };
  return <section className="liveAnalysis-files">
    <div className="liveAnalysis-sectionHeading"><strong>Generated files</strong><span>{artifacts.length}</span></div>
    {artifacts.map((artifact, index) => <div className="liveAnalysis-file" key={artifact.id || artifact.storagePath || index}>
      <div><strong>{artifact.name || artifact.relativePath || "Output file"}</strong><small>{formatBytes(artifact.size)}</small></div>
      <button type="button" disabled={(!artifact.storagePath && !artifact.path) || downloading[artifact.id]} onClick={() => download(artifact)}>{downloading[artifact.id] ? "Opening…" : "Download"}</button>
    </div>)}
  </section>;
}

export default function LiveAnalysisWorkspace({ job, requestedTab = "", tabActivationKey = 0 }) {
  const script = job?.analysis?.script || EMPTY_DOCUMENT;
  const methods = job?.analysis?.methods;
  const investigationCompleteValue = methods?.investigationComplete ?? job?.analysis?.investigationComplete;
  const methodsReady = methods?.status === "complete";
  const methodsFinal = methodsReady && (investigationCompleteValue === true || (!latestCausalFinding(methods) && investigationCompleteValue !== false));
  const terminalStatus = TERMINAL.has(job?.status) ? job.status : "";
  const step = currentStepLabel(job);
  const [activeTab, setActiveTab] = useState("activity");
  const bodyRef = useRef(null);
  const bodyNearBottomRef = useRef(true);
  const jobRef = useRef("");
  const documentStatusRef = useRef("waiting");
  const documentRevisionRef = useRef("");
  const methodsRevisionRef = useRef("");
  const liveDraftsRef = useRef(false);
  const manualTabRef = useRef(false);
  const handledTabRequestRef = useRef("");
  const liveDraftsCanLead = !terminalStatus && !["presenting_result", "uploading_artifacts"].includes(job?.stage);
  const methodSteps = methodEntries(methods?.steps);
  const methodsRevision = methodsReady
    ? `${methods?.updatedAt || ""}:${methodSteps.length}:${JSON.stringify(methodSteps.at(-1) || null)}` : "";
  const documentRevision = `${script.status}:${script.updatedAt || ""}:${script.content?.length || 0}`;
  const activityKey = activityItems(job).map((entry) => `${entry.id}:${entry.sequence || ""}:${entry.detail || ""}`).join("|");

  useEffect(() => {
    const previous = documentStatusRef.current;
    const methodsChanged = methodsReady && methodsRevision !== methodsRevisionRef.current;
    const documentChanged = documentRevision !== documentRevisionRef.current;
    if (jobRef.current !== job?.id) {
      jobRef.current = job?.id || "";
      manualTabRef.current = false;
      const initialTab = ["failed", "timed_out"].includes(terminalStatus) || script.status === "failed" ? "activity"
        : methodsFinal ? "methods"
          : liveDraftsCanLead && script.status === "streaming" ? "code" : "activity";
      bodyNearBottomRef.current = initialTab !== "activity";
      setActiveTab(initialTab);
    } else if (["failed", "timed_out"].includes(terminalStatus) || (script.status === "failed" && previous !== "failed")) {
      bodyNearBottomRef.current = false;
      manualTabRef.current = false;
      setActiveTab("activity");
    } else if (!manualTabRef.current && methodsChanged) {
      bodyNearBottomRef.current = false;
      setActiveTab("methods");
    } else if (!manualTabRef.current && liveDraftsCanLead && script.status === "streaming" && documentChanged) {
      bodyNearBottomRef.current = true;
      setActiveTab("code");
    } else if (!manualTabRef.current && !methodsFinal && (
      (previous === "streaming" && script.status !== "streaming")
      || (liveDraftsRef.current && !liveDraftsCanLead)
    )) {
      bodyNearBottomRef.current = false;
      setActiveTab("activity");
    }
    documentStatusRef.current = script.status;
    documentRevisionRef.current = documentRevision;
    methodsRevisionRef.current = methodsRevision;
    liveDraftsRef.current = liveDraftsCanLead;
  }, [job?.id, liveDraftsCanLead, script.status, documentRevision, methodsReady, methodsRevision, methodsFinal, terminalStatus]);

  useEffect(() => {
    if (!["activity", "methods"].includes(requestedTab)) return;
    const requestKey = `${job?.id}:${tabActivationKey}`;
    if (handledTabRequestRef.current === requestKey) return;
    handledTabRequestRef.current = requestKey;
    manualTabRef.current = requestedTab !== "activity";
    bodyNearBottomRef.current = false;
    setActiveTab(requestedTab);
  }, [job?.id, requestedTab, tabActivationKey]);

  useEffect(() => {
    const node = bodyRef.current;
    const activeDocument = activeTab === "code" ? script : null;
    if (node && activeDocument?.status === "streaming" && bodyNearBottomRef.current) {
      node.scrollTop = node.scrollHeight;
    }
  }, [activeTab, script]);

  useEffect(() => {
    const node = bodyRef.current;
    if (node && activeTab === "activity") node.scrollTop = node.scrollHeight;
  }, [activeTab, activityKey]);

  const selectTab = (tab) => {
    manualTabRef.current = tab !== "activity";
    bodyNearBottomRef.current = false;
    setActiveTab(tab);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  };

  return <div className="liveAnalysis-workspace">
    <div className={`liveAnalysis-currentStep ${terminalStatus ? `is-${terminalStatus}` : "isActive"}`}>
      <span>{terminalStatus ? statusIcon("waiting", terminalStatus) : "●"}</span>
      <div><small>Current step</small><strong>{step}</strong></div>
    </div>
    <nav className="liveAnalysis-tabs" aria-label="Analysis details">
      {["activity", "code", "methods", "files"].map((tab) => <button key={tab} type="button" className={activeTab === tab ? "isActive" : ""} onClick={() => selectTab(tab)}>
        {tab === "files" ? `Files${job?.artifacts?.length ? ` (${job.artifacts.length})` : ""}` : tab[0].toUpperCase() + tab.slice(1)}
        {tab === "code" && script.status === "streaming" && !terminalStatus ? <i /> : null}
      </button>)}
    </nav>
    <div className="liveAnalysis-body" ref={bodyRef} onScroll={(event) => {
      const node = event.currentTarget;
      bodyNearBottomRef.current = node.scrollHeight - node.scrollTop - node.clientHeight < 48;
    }}>
      {activeTab === "activity" && <ActivityTimeline job={job} onOpenDocument={() => selectTab("code")} />}
      {activeTab === "methods" && <MethodsView methods={methods} terminalStatus={terminalStatus} />}
      {activeTab === "code" && <DocumentView document={script} terminalStatus={terminalStatus} />}
      {activeTab === "files" && (job?.artifacts?.length ? <GeneratedFiles artifacts={job.artifacts} /> : <div className="liveAnalysis-empty">No generated files yet.</div>)}
    </div>
  </div>;
}
